import { FormControl, FormLabel, Radio, Stack, Theme, useTheme } from '@mui/joy';
import { FieldValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { IContactGroupAddress } from 'contacts/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { ButtonGroup } from 'core/elements';
import { ActionButtonType } from 'core/enums';
import { Color, Size, Variant } from 'theme/enums';

import { IFieldConfig } from './types';

interface IProps {
  disabled?: boolean;
  field: IFieldConfig;
  locations: IContactGroupAddress[];
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
}

export const LocationField = ({ field, locations, watch, setValue, disabled }: IProps) => {
  const theme = useTheme();

  const handleGetValueKey = (location: IContactGroupAddress) => {
    const value = field.getValueKey ? field.getValueKey(location) : location[field.valueKey];

    return {
      id: location.addressId,
      value,
    };
  };

  const handleGetLabelKey = (location: IContactGroupAddress) => {
    if (field.getLabelKey) {
      return field.getLabelKey(location) || DEFAULT_PLACEHOLDER;
    }

    return location[field.labelKey] || DEFAULT_PLACEHOLDER;
  };

  const watchedValue = watch(field.name);

  return (
    <Stack gap={0.25}>
      <FormControl size={Size.SM}>
        <FormLabel required={field.required || false}>{field.label}</FormLabel>
      </FormControl>

      <ButtonGroup
        buttons={locations.map((location) => {
          const isChecked = watchedValue?.id === location.addressId;

          const value = handleGetValueKey(location);
          const label = handleGetLabelKey(location);

          return {
            type: ActionButtonType.BUTTON,
            color: Color.NEUTRAL,
            variant: Variant.OUTLINED,
            label: (
              <Radio
                name={field.name}
                size={Size.SM}
                value={value}
                label={label}
                checked={isChecked}
                disabled={disabled}
                sx={getRadioChoiceSxProps(theme, isChecked)}
              />
            ),
            onClick: () => !disabled && setValue(field.name, value),
            sx: getRadioButtonSxProps(theme),
          };
        })}
        sx={{
          '--ButtonGroup-separatorColor': theme.palette.neutral[200],
        }}
      />
    </Stack>
  );
};

const getRadioChoiceSxProps = (theme: Theme, isChecked: boolean) => ({
  fontWeight: 400,
  color: `${theme.palette.neutral[isChecked ? 800 : 600]} !important`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'left',

  '& label': {
    overflowWrap: 'anywhere',
  },
});

const getRadioButtonSxProps = (theme: Theme) => ({
  padding: '10px 8px',
  justifyContent: 'flex-start',
  alignItems: 'center',
  backgroundColor: theme.palette.neutral[50],
  borderColor: theme.palette.neutral[200],
  minHeight: '48px',
  width: '50%',
});
